import {
  surchargeRuleSetCreateSchema,
  type SurchargeRuleSetCreateInput,
} from '@hirebotiq-ops/core';

export const SURCHARGE_CONDITION_KINDS = [
  'always',
  'weekdays',
  'holiday',
  'special_days',
] as const;

export type SurchargeConditionKind = (typeof SURCHARGE_CONDITION_KINDS)[number];

export type SurchargeRuleDraft = {
  key: string;
  percent: string;
  conditionKind: SurchargeConditionKind;
  weekdays: number[];
  specialDays: string;
  useWindow: boolean;
  windowFrom: string;
  windowTo: string;
  onlyIfShiftStartedBeforeMidnight: boolean;
};

export type SurchargeRuleSetDraft = {
  name: string;
  base: 'wage' | 'bill_rate';
  stackingPolicy: 'cumulative' | 'highest_only';
  clientId?: string;
  validFrom: string;
  notes: string;
  rules: SurchargeRuleDraft[];
};

export function emptySurchargeRuleDraft(): SurchargeRuleDraft {
  return {
    key: '',
    percent: '',
    conditionKind: 'always',
    weekdays: [1, 2, 3, 4, 5],
    specialDays: '',
    useWindow: false,
    windowFrom: '20:00',
    windowTo: '06:00',
    onlyIfShiftStartedBeforeMidnight: false,
  };
}

export function parseSpecialDays(value: string): Array<{
  month: number;
  day: number;
  fromLocalTime?: string;
}> {
  const tokens = value
    .split(/[\n,]+/)
    .map((token) => token.trim())
    .filter(Boolean);
  if (tokens.length === 0) {
    throw new Error('Mindestens einen Sondertag im Format MM-TT oder MM-TT@HH:MM angeben.');
  }

  return tokens.map((token) => {
    const match = /^(\d{1,2})-(\d{1,2})(?:@(\d{1,2}):(\d{2}))?$/.exec(token);
    if (!match) {
      throw new Error(`Sondertag „${token}“ hat nicht das Format MM-TT oder MM-TT@HH:MM.`);
    }
    const month = Number(match[1]);
    const day = Number(match[2]);
    const hour = match[3] == null ? undefined : Number(match[3]);
    const minute = match[4] == null ? undefined : Number(match[4]);
    // Max. Tag je Monat (Feb = 29, Sondertage gelten jahresübergreifend) —
    // sonst legt die UI stumm nie greifende Regeln wie 02-30 an.
    const maxDayInMonth = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    if (month < 1 || month > 12 || day < 1 || day > (maxDayInMonth[month - 1] ?? 31)) {
      throw new Error(`Sondertag „${token}“ enthält kein gültiges Monats-/Tagesformat.`);
    }
    if (hour != null && (hour < 0 || hour > 23 || minute == null || minute > 59)) {
      throw new Error(`Sondertag „${token}“ enthält keine gültige Uhrzeit.`);
    }
    return {
      month,
      day,
      ...(hour != null && minute != null
        ? { fromLocalTime: `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}` }
        : {}),
    };
  });
}

function buildCondition(
  rule: SurchargeRuleDraft,
): SurchargeRuleSetCreateInput['rules'][number]['condition'] {
  switch (rule.conditionKind) {
    case 'weekdays':
      return { kind: 'weekdays', days: [...rule.weekdays].sort((a, b) => a - b) };
    case 'holiday':
      return { kind: 'holiday' };
    case 'special_days':
      return { kind: 'special_days', days: parseSpecialDays(rule.specialDays) };
    default:
      return { kind: 'always' };
  }
}

export function buildSurchargeRuleSetInput(
  draft: SurchargeRuleSetDraft,
): SurchargeRuleSetCreateInput {
  const input = {
    name: draft.name.trim(),
    base: draft.base,
    stackingPolicy: draft.stackingPolicy,
    ...(draft.clientId ? { clientId: draft.clientId } : {}),
    validFrom: draft.validFrom,
    ...(draft.notes.trim() ? { notes: draft.notes.trim() } : {}),
    rules: draft.rules.map((rule) => ({
      key: rule.key.trim(),
      condition: buildCondition(rule),
      percent: Number(rule.percent),
      ...(rule.useWindow
        ? { window: { from: rule.windowFrom.trim(), to: rule.windowTo.trim() } }
        : {}),
      ...(rule.onlyIfShiftStartedBeforeMidnight
        ? { onlyIfShiftStartedBeforeMidnight: true }
        : {}),
    })),
  };

  return surchargeRuleSetCreateSchema.parse(input);
}
